import z from 'zod/v4';
import { $ZodType, $ZodTypes } from 'zod/v4/core';

export type ZodShape = Record<string, z.ZodType>;

export type FormattedZodIssue = {
  field: string;
  message: string;
};

export function getObjectShape(schema: z.ZodObject): ZodShape {
  return schema.shape;
}

export function omitObjectSchemaKeys(
  value: Record<string, unknown>,
  schema: z.ZodObject,
): Record<string, unknown> {
  const keys = new Set(Object.keys(getObjectShape(schema)));
  return Object.fromEntries(Object.entries(value).filter(([key]) => !keys.has(key)));
}

export function getZodDef(schema: $ZodType) {
  return (schema as $ZodTypes)._zod.def;
}

export function isConfigurableSchema(schema: z.ZodType): schema is z.ZodObject {
  const def = getZodDef(schema);
  return def.type === 'object' && Object.keys(def.shape).length > 0;
}

export function getSchemaFieldPaths(schema: z.ZodObject): string[][] {
  return Object.entries(getObjectShape(schema)).flatMap(([key, keySchema]) => {
    const unwrappedSchema = unwrapTransparentSchema(keySchema);
    if (unwrappedSchema instanceof z.ZodObject) {
      const childPaths = getSchemaFieldPaths(unwrappedSchema);
      if (childPaths.length === 0) {
        return [[key]];
      }
      return childPaths.map((path) => [key, ...path]);
    }
    return [[key]];
  });
}

export function isOptionalSchema(schema: z.ZodType): boolean {
  return schema.safeParse(undefined).success;
}

export function isOmittableSchema(schema: z.ZodType): boolean {
  return isOptionalSchema(schema);
}

export function unwrapTransparentSchema(schema: z.ZodType): z.ZodType;
export function unwrapTransparentSchema(schema: $ZodType): $ZodType;
export function unwrapTransparentSchema(schema: $ZodType): $ZodType {
  let currentSchema = schema;

  while (true) {
    const def = getZodDef(currentSchema);
    switch (def.type) {
      case 'optional':
      case 'nullable':
      case 'default':
      case 'prefault':
      case 'catch': {
        currentSchema = def.innerType;
        continue;
      }
      default: {
        return currentSchema;
      }
    }
  }
}

export function getShapeField(shape: ZodShape, key: string): z.ZodType {
  if (!Object.prototype.hasOwnProperty.call(shape, key)) {
    throw new Error(`Unknown schema shape key "${key}"`);
  }
  return shape[key] as z.ZodType;
}

export function formatZodIssues(error: z.ZodError): FormattedZodIssue[] {
  return error.issues.map((issue) => ({
    field: formatZodIssuePath(issue.path),
    message: issue.message,
  }));
}

export function formatZodIssuePath(path: PropertyKey[]): string {
  return path.reduce<string>((formattedPath, segment) => {
    if (typeof segment === 'number') {
      return `${formattedPath}[${segment}]`;
    }

    const segmentText = String(segment);
    return formattedPath ? `${formattedPath}.${segmentText}` : segmentText;
  }, '');
}
